import { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import { type Product, ProductItem, Treatment } from '../../global-nav-products-dropdown';
import { MobileNavCard } from '../../mobile-nav/mobile-nav-card';
import { useAnalytics } from '../hooks/use-analytics';

import { BaseGrid } from './base-grid';

// Wrapper component to add analytics tracking to MobileNavCard
// MobileNavCard returns an <a> tag, so we wrap it in an <li> and attach the click handler.
const MobileNavCardWithAnalytics = ({
	product,
	description,
	isNew,
	onCardClick,
}: {
	product: Product;
	description: string;
	isNew: boolean;
	onCardClick: () => void;
}) => {
	const liRef = useRef<HTMLLIElement>(null);

	useEffect(() => {
		const anchor = liRef.current?.querySelector('a');
		if (anchor) {
			const handleClick = () => {
				onCardClick();
				// Don't prevent default - let the anchor handle navigation
			};
			anchor.addEventListener('click', handleClick);
			return () => {
				anchor.removeEventListener('click', handleClick);
			};
		}
	}, [onCardClick]);

	return (
		<li ref={liRef}>
			<MobileNavCard {...product} description={description} isNew={isNew} productKey={'home'} />
		</li>
	);
};

// Wrapper component to add analytics tracking to ProductItem
// ProductItem already returns an <li>, so we can't wrap it in another element.
// Instead, we use the product id to find the anchor and attach the click handler.
const ProductItemWithAnalytics = ({
	product,
	onCardClick,
}: {
	product: Product;
	onCardClick: () => void;
}) => {
	useEffect(() => {
		// Find the anchor by the product id (ProductItem sets id on the <li>)
		const li = document.getElementById(product.id);
		const anchor = li?.querySelector('a');
		if (anchor) {
			const handleClick = () => {
				onCardClick();
				// Don't prevent default - let the anchor handle navigation
			};
			anchor.addEventListener('click', handleClick);
			return () => {
				anchor.removeEventListener('click', handleClick);
			};
		}
	}, [product.id, onCardClick]);

	return <ProductItem {...product} />;
};

type ProductsGridProps = {
	products: Product[];
	title: string;
	isMobile: boolean;
	container: 'ProductsRecommendationsContainer' | 'NewProductsContainer';
};

export const ProductsGrid = ({ products, title, isMobile, container }: ProductsGridProps) => {
	const intl = useIntl();

	const { fireCardClicked } = useAnalytics();

	const handleCardClick = (product: Product) => {
		fireCardClicked({
			container,
			product,
			isMobile,
		});
	};

	const productItems = products.map((product: Product) => {
		if (isMobile) {
			return (
				<MobileNavCardWithAnalytics
					key={product.id}
					product={product}
					description={intl.formatMessage(product.description)}
					isNew={product.treatment === Treatment.New}
					onCardClick={() => handleCardClick(product)}
				/>
			);
		}

		// For desktop, ProductItem already returns an <li> with an <a> inside.
		// We use it directly and attach analytics via a wrapper component.
		return (
			<ProductItemWithAnalytics
				key={product.id}
				product={product}
				onCardClick={() => handleCardClick(product)}
			/>
		);
	});

	return <BaseGrid products={productItems} title={title} isMobile={isMobile} />;
};
